/**
 * Dataset table component with pagination.
 */

import { formatDate } from '@/utils/helpers';
import { useTranslation } from 'react-i18next';
import { Pagination, Table } from 'react-bootstrap';

interface Dataset {
  id: number;
  device_id_str: string;
  data_type_name: string;
  start_time: string;
  end_time: string;
  created_at: string;
}

interface DatasetsTableProps {
  data: any;
  onSelectDataset: (datasetId: number) => void;
  selectedDataset: number | null;
  onPageChange: (page: number) => void;
}

export default function DatasetsTable({
  data,
  onSelectDataset,
  selectedDataset,
  onPageChange,
}: DatasetsTableProps) {
  const { t } = useTranslation();

  if (!data || !data.items || data.items.length === 0) {
    return (
      <div>
        <p>{t('datasets.noDatasetsFound')}</p>
        {data?.total > 0 && (
          <small className="text-muted">{t('datasets.accessTo', { count: data.total })}</small>
        )}
      </div>
    );
  }

  const datasets = data.items as Dataset[];
  const { page = 1, pages = 1, total = datasets.length } = data;

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pages) {
      onPageChange(newPage);
    }
  };

  return (
    <div>
      <p className="mb-3" style={{ color: 'var(--myant-text-secondary)', fontWeight: 500 }}>
        {t('datasets.showing', { count: datasets.length, total })}
      </p>
      <div style={{ borderRadius: 'var(--myant-radius-lg)', overflow: 'hidden', border: '1px solid var(--myant-border-light)' }}>
        <Table striped hover responsive style={{ marginBottom: 0 }}>
          <thead>
            <tr>
              <th style={{ fontWeight: 600 }}>{t('datasets.deviceId')}</th>
              <th style={{ fontWeight: 600 }}>{t('datasets.dataType')}</th>
              <th style={{ fontWeight: 600 }}>{t('datasets.startTime')}</th>
              <th style={{ fontWeight: 600 }}>{t('datasets.endTime')}</th>
              <th style={{ fontWeight: 600 }}>{t('datasets.createdAt')}</th>
            </tr>
          </thead>
          <tbody>
            {datasets.map((dataset) => (
              <tr
                key={dataset.id}
                onClick={() => onSelectDataset(dataset.id)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: selectedDataset === dataset.id ? 'var(--myant-primary-light)' : undefined,
                  transition: 'background-color 0.2s ease-in-out',
                }}
              >
                <td>{dataset.device_id_str}</td>
                <td>{dataset.data_type_name}</td>
                <td>{formatDate(dataset.start_time)}</td>
                <td>{formatDate(dataset.end_time)}</td>
                <td>{formatDate(dataset.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <div className="mt-3 d-flex justify-content-between align-items-center">
        <small style={{ color: 'var(--myant-text-secondary)' }}>
          {t('datasets.pageOf', { page, pages, total })}
        </small>
        {pages > 1 && (
          <Pagination className="mb-0">
            <Pagination.First onClick={() => handlePageChange(1)} disabled={page === 1} />
            <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 1} />
            {Array.from({ length: Math.min(5, pages) }, (_, i) => {
              let pageNum;
              if (pages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= pages - 2) {
                pageNum = pages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === page}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </Pagination.Item>
              );
            })}
            <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === pages} />
            <Pagination.Last onClick={() => handlePageChange(pages)} disabled={page === pages} />
          </Pagination>
        )}
      </div>
    </div>
  );
}
